import React, { useCallback, useMemo, memo } from 'react';
import { Table, Space, Card, Tooltip, Popconfirm } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import useFooterMenus from '../useFooterMenus';
import { Button } from 'components';
import { FooterMenusFilters } from './FooterMenusFilters';
import { EmptyState } from './EmptyState';

const renderTitleValue = (record) => {
  const title = record.title_it || record.title || '-';
  const value = record.menu_value_it || record.menu_value;

  if (record.menu_type === 'link' && value) {
    return (
      <a
        href={value}
        target={record.open_in_new_tab ? '_blank' : undefined}
        rel='noreferrer'
        onClick={(e) => e.stopPropagation()}
      >
        {title}
      </a>
    );
  }

  return title;
};

export const FooterMenusList = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);

  const {
    footerMenus,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingShoppingCenters,
    fetchFooterMenus,
    deleteFooterMenu,
    handleTableChange,
    updateFilters,
  } = useFooterMenus();

  const handleDelete = useCallback(
    async (id) => {
      try {
        const success = await deleteFooterMenu(id);
        if (success) {
          fetchFooterMenus({
            page: currentPage,
            per: pageSize,
            ...filters,
          });
        }
      } catch (err) {
        console.error('Error deleting footer menu:', err);
      }
    },
    [
      deleteFooterMenu,
      fetchFooterMenus,
      currentPage,
      pageSize,
      filters,
    ],
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      updateFilters(newFilters);
    },
    [updateFilters],
  );

  const columns = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_center',
        key: 'shopping_center',
        render: (shoppingCenter) => shoppingCenter?.title || '-',
      },
      {
        title: t('Titolo'),
        dataIndex: 'title_it',
        key: 'title_it',
        render: (_, record) => renderTitleValue(record),
      },
      {
        title: t('Tipo'),
        dataIndex: 'menu_type',
        key: 'menu_type',
        render: (menuType) =>
          menuType === 'link' ? t('Link') : t('Testo'),
      },
      {
        title: t('Valore'),
        dataIndex: 'menu_value_it',
        key: 'menu_value_it',
        render: (text, record) => {
          if (record.menu_type === 'link') {
            return text || '-';
          }
          if (!text) return '-';
          return text.length > 80 ? `${text.substring(0, 80)}...` : text;
        },
      },
      {
        title: t('Azioni'),
        key: 'actions',
        width: 180,
        render: (_, record) => (
          <Space>
            {(pages?.ability?.can_read !== false ||
              record.can_read !== false) && (
              <Tooltip title={t('Visualizza')}>
                <Button
                  type='secondary'
                  icon={<EyeOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/website/footer_menus/${record.id}`);
                  }}
                />
              </Tooltip>
            )}
            {record.can_update !== false && (
              <Tooltip title={t('Modifica')}>
                <Button
                  type='secondary'
                  icon={<EditIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/website/footer_menus/edit/${record.id}`);
                  }}
                />
              </Tooltip>
            )}
            {record.can_destroy !== false && (
              <Popconfirm
                title={t('Sei sicuro di voler cancellare?')}
                okText={t('Si, cancella')}
                cancelText={t('No, lascia')}
                onConfirm={() => handleDelete(record.id)}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={t('Elimina')}>
                    <Button type='red' icon={<DeleteIcon />} />
                  </Tooltip>
                </span>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, handleDelete, pages?.ability?.can_read],
  );

  const pagination = useMemo(
    () => ({
      current: currentPage,
      pageSize: pageSize,
      total: total,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (totalCount, range) =>
        t('Paginazione elementi', {
          start: range[0],
          end: range[1],
          total: totalCount,
        }),
    }),
    [currentPage, pageSize, total, t],
  );

  return (
    <Card
      title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('Footer menu')}
        </span>
      }
      extra={
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => navigate('/website/footer_menus/create')}
          style={{
            marginBottom: 12,
            width: '140px',
            height: 48,
            backgroundColor: '#002637',
            borderColor: '#002637',
          }}
        >
          {t('Nuovo')}
        </Button>
      }
    >
      <FooterMenusFilters
        onFiltersChange={handleFiltersChange}
        shoppingCenters={shoppingCenters}
        loadingShoppingCenters={loadingShoppingCenters}
        filters={filters}
      />

      <div>
        {!!footerMenus?.length && (
          <Table
            columns={columns}
            dataSource={footerMenus}
            pagination={pagination}
            onChange={handleTableChange}
            loading={loading}
            rowKey='id'
            onRow={(record) => ({
              onClick: () => {
                if (record?.id) {
                  navigate(`/website/footer_menus/${record.id}`);
                }
              },
              style: { cursor: 'pointer' },
            })}
          />
        )}
        {!footerMenus?.length && !loading && (
          <Card>
            <EmptyState />
          </Card>
        )}
      </div>
    </Card>
  );
});

export default FooterMenusList;
